/**
 * API-safe version of site-settings that doesn't rely on server components
 * This is used by the i18n module to avoid client component issues
 */

import { SiteSettings } from '@/types/database'
import { createClient as createSupabaseClient } from '@supabase/supabase-js'

// Default site settings
const DEFAULT_SETTINGS: Omit<SiteSettings, 'id' | 'updated_at'> = {
  site_name: 'COMMERCE',
  site_logo: '/full-logo.webp',
  dark_mode_logo: '/full-logo-dark.webp',
  email_site_logo: '/full-logo-dark.png',
  email_dark_mode_logo: '/full-logo.png',
  favicon: '/favicon.ico',
  site_url: process.env.NEXT_PUBLIC_SITE_URL || 'https://www.commerce.space',
  primary_color: '#8b5cf6',
  secondary_color: '#3b82f6',
  accent_color: '#ec4899',
  background_color: '#020014',
  text_color: '#efedfd',
  light_mode_surface_color: '#f8fafc',
  light_mode_surface_hover_color: '#f1f5f9',
  light_mode_surface_active_color: '#e2e8f0',
  company_address: 'Lvovo St. 105A, Vilnius, Lithuania, 08104',
  support_email: 'contact@commerce.space',
  locales: ['en', 'es', 'de', 'fr', 'it', 'nl', 'pl', 'se'],
  default_locale: 'en',
  currency_conversion_rates: {
    USD: 1.1,
    EUR: 1,
    GBP: 0.85,
    CAD: 1.4,
    AUD: 1.5,
    NZD: 1.6,
    CHF: 1.05,
    SEK: 10.5,
    PLN: 4.5
  }
}

// Disable Edge caching for this API call with cache header
const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
  'Pragma': 'no-cache',
  'Expires': '0'
};

/**
 * API-safe version of getSiteSettings that doesn't rely on server components
 */
export async function getApiSiteSettings(): Promise<SiteSettings> {
  try {
    const supabase = createSupabaseClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      {
        auth: {
          persistSession: false
        },
        global: {
          headers: NO_CACHE_HEADERS
        }
      }
    )
    
    const { data, error } = await supabase
      .from('site_settings')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(1)
      .single()
    
    if (error || !data) {
      return {
        id: 'default',
        updated_at: new Date().toISOString(),
        ...DEFAULT_SETTINGS
      }
    }
    
    // Properly merge currency conversion rates - this ensures default rates are preserved
    // for currencies not specified in the database
    const mergedCurrencyRates = {
      ...DEFAULT_SETTINGS.currency_conversion_rates,
      ...(data.currency_conversion_rates || {})
    };
    
    // Merge with defaults to ensure all properties exist, with special handling for currency rates
    return {
      ...DEFAULT_SETTINGS,
      ...data,
      currency_conversion_rates: mergedCurrencyRates
    }
  } catch {
    return {
      id: 'default',
      updated_at: new Date().toISOString(),
      ...DEFAULT_SETTINGS
    }
  }
} 